import { companyRequest } from '@/company/api';
import { namespaceCopyFor } from '@/i18n/ns';
import workspaceKnowledgeRuntimeCopy from '@/i18n/ns/workspace-knowledge-runtime';

// Agents own their instructions and optional reusable Skills, and run by
// hand or on a schedule. The server decides every permission (agentAccess);
// the UI only shows what `access` says.

export interface AgentAccess {
  see: boolean;
  run: boolean;
  edit: boolean;
  admin: boolean;
}
export interface AgentPerson {
  userId: string;
  name: string | null;
}
export interface AgentSchedule {
  cron: string;
  timezone: string;
}
export type AgentScope = 'personal' | 'company';
export type AgentResultMode = 'new' | 'keep_updated';
export type AgentTriggerType = 'manual' | 'schedule' | 'test';
export type AgentRunStatus =
  | 'queued'
  | 'running'
  | 'awaiting_approval'
  | 'completed'
  | 'failed'
  | 'cancelled';

export interface AgentPinnedSkill {
  id: string;
  name: string | null;
  published: boolean;
  publishedRevision: number | null;
}
export interface AgentRevision {
  revision: number;
  skill: { id: string; name: string | null; published: boolean; publishedRevision: number | null };
  goal: string;
  expectedOutput: string;
  boundaries: string;
  resources: string;
  skillIds: string[];
  /** Every Skill it always follows, named by its published version (null name: not published). */
  skills: AgentPinnedSkill[];
  instructions: string;
  schedule: AgentSchedule | null;
  emailOnFinish: boolean;
  resultMode: AgentResultMode;
  note: string | null;
  author: AgentPerson | null;
  createdAt: string | null;
}
export interface AgentRun {
  id: string;
  status: AgentRunStatus;
  triggerType: AgentTriggerType;
  agentRevision: number;
  runBy: AgentPerson | null;
  createdAt: string | null;
  scheduledFor: string | null;
  startedAt: string | null;
  finishedAt: string | null;
  durationMs: number | null;
  coalescedFires: number | null;
  toolCalls: number;
  errorCode: string | null;
  error: string | null;
  threadId: string | null;
  resultId: string | null;
  /** The opening of the run's answer, when you may read its run chat. */
  summary: string | null;
  /** The connections the run could not read, when you may read its run chat. */
  unavailableSources?: string[];
  /** The credits the run used, only for the person it ran for and whoever manages billing. */
  credits?: number;
}
export interface AgentEditor {
  userId: string;
  name: string | null;
  status: 'requested' | 'granted';
  requestedAt: string;
  decidedAt: string | null;
}
export type AgentSharingVisibility = 'private' | 'company' | 'members';
export interface AgentAudienceEntry {
  userId: string | null;
  role: 'viewer' | 'editor';
}
export interface AgentSharing {
  visibility: AgentSharingVisibility;
  audience: AgentAudienceEntry[];
  companyRole?: 'viewer' | 'editor';
}
export interface Agent {
  id: string;
  name: string;
  description: string;
  scope: AgentScope;
  sharing: AgentSharing;
  enabled: boolean;
  owner: AgentPerson | null;
  companyOwned: boolean;
  companyOwnedAt: string | null;
  currentRevision: number;
  publishedRevision: number | null;
  published: AgentRevision | null;
  draft: AgentRevision | null;
  trigger: {
    id: string;
    schedule: AgentSchedule;
    enabled: boolean;
    nextFireAt: string | null;
    lastFiredAt: string | null;
  } | null;
  nextFireAt: string | null;
  maintainedResultId: string | null;
  lastRun: AgentRun | null;
  access: AgentAccess;
  isOwner: boolean;
  manages: boolean;
  myEditAccess: 'requested' | 'granted' | null;
  editors: AgentEditor[] | null;
  /** The server's answer: you tried this exact draft to completion, so you may publish it. */
  canPublish: boolean;
  createdAt: string | null;
  updatedAt: string | null;
}
export type AgentGroup = 'yours' | 'company' | 'others';
export interface AgentListRow {
  id: string;
  name: string;
  scope: AgentScope;
  enabled: boolean;
  owner: AgentPerson | null;
  companyOwned: boolean;
  schedule: AgentSchedule | null;
  access: AgentAccess;
  group: AgentGroup;
  sharing: AgentSharing;
  // Absent on 'others' rows: an admin sees only name, owner, schedule and on/off.
  isOwner?: boolean;
  description?: string;
  publishedRevision?: number | null;
  currentRevision?: number;
  skill?: { id: string; name: string | null; published: boolean } | null;
  nextFireAt?: string | null;
  lastRun?: AgentRun | null;
  myEditAccess?: 'requested' | 'granted' | null;
}
export interface DiffLine {
  type: 'same' | 'add' | 'remove';
  text: string;
}
export interface AgentChange<T> {
  before: T;
  after: T;
}
export interface AgentDiff {
  goal?: AgentChange<string> | null;
  expectedOutput?: AgentChange<string> | null;
  resources?: AgentChange<string> | null;
  boundaries?: AgentChange<string> | null;
  skillIds?: AgentChange<string[]> | null;
  skill: AgentChange<{ id: string; name: string | null } | null> | null;
  schedule: AgentChange<AgentSchedule | null> | null;
  emailOnFinish: AgentChange<boolean> | null;
  resultMode: AgentChange<AgentResultMode> | null;
  instructions: DiffLine[] | null;
}
export interface AgentVersion extends AgentRevision {
  live?: boolean;
  diff: AgentDiff;
}
export interface AgentVersions {
  versions: AgentVersion[];
  draft: AgentVersion | null;
}
export interface AgentDefinition {
  goal: string;
  expectedOutput: string;
  boundaries: string;
  resources: string;
  skillIds: string[];
  instructions: string;
  emailOnFinish: boolean;
  resultMode: AgentResultMode;
}

const root = '/api/v1/company/agents';
/** The runs the canvas lists (the server's most). */
export const RUNS_SHOWN = 50;
const one = (id: string) => `${root}/${encodeURIComponent(id)}`;

export const agentsApi = {
  list: (q = '') =>
    companyRequest<{ agents: AgentListRow[] }>(
      q.trim() ? `${root}?q=${encodeURIComponent(q.trim())}` : root,
    ).then((body) => body.agents),
  get: (id: string) => companyRequest<{ agent: Agent }>(one(id)).then((body) => body.agent),
  create: (
    input: AgentDefinition & {
      name: string;
      description?: string;
      scope: AgentScope;
      schedule?: AgentSchedule | null;
      enabled?: boolean;
      /** Saved unpublished with its schedule off, until someone creates it. */
      draft?: boolean;
    },
  ) => companyRequest<{ agent: Agent }>(root, 'POST', input).then((body) => body.agent),
  /** The agent, or only its list row when the change took it out of your view. */
  patch: (
    id: string,
    input: Partial<{ name: string; description: string; enabled: boolean; scope: AgentScope }>,
  ) =>
    companyRequest<{ agent: Agent | Pick<Agent, 'id' | 'scope' | 'enabled'> }>(
      one(id),
      'PATCH',
      input,
    ).then((body) => body.agent),
  update: (
    id: string,
    input: AgentDefinition & { baseRevision: number; name?: string; description?: string },
  ) =>
    companyRequest<{ agent: Agent }>(`${one(id)}/definition`, 'PUT', input).then(
      (body) => body.agent,
    ),
  saveDraft: (
    id: string,
    input: AgentDefinition & { baseRevision: number; name?: string; description?: string },
  ) =>
    companyRequest<{ agent: Agent }>(`${one(id)}/draft`, 'PUT', input).then((body) => body.agent),
  setTrigger: (
    id: string,
    input: { schedule: { cron: string; timezone?: string } | null; enabled: boolean },
  ) =>
    companyRequest<{ agent: Agent }>(`${one(id)}/trigger`, 'PUT', input).then((body) => body.agent),
  tryDraft: (id: string) =>
    companyRequest<{ runId: string; threadId: string }>(`${one(id)}/draft/try`, 'POST', {}),
  publish: (id: string, input: { revision: number; note: string }) =>
    companyRequest<{ agent: Agent }>(`${one(id)}/publish`, 'POST', input).then(
      (body) => body.agent,
    ),
  versions: (id: string) => companyRequest<AgentVersions>(`${one(id)}/versions`),
  restore: (id: string, revision: number) =>
    companyRequest<{ agent: Agent }>(`${one(id)}/versions/${revision}/restore`, 'POST', {}).then(
      (body) => body.agent,
    ),
  archive: (id: string) => companyRequest<{ id: string; archived: boolean }>(one(id), 'DELETE'),
  run: (id: string) =>
    companyRequest<{ runId: string; threadId: string }>(`${one(id)}/run`, 'POST', {}),
  cancelRun: (id: string, runId: string) =>
    companyRequest<{ run: AgentRun }>(
      `${one(id)}/runs/${encodeURIComponent(runId)}`,
      'DELETE',
    ).then((body) => body.run),
  runs: (id: string, limit = RUNS_SHOWN) =>
    companyRequest<{ runs: AgentRun[] }>(`${one(id)}/runs?limit=${limit}`).then(
      (body) => body.runs,
    ),
  copy: (id: string) =>
    companyRequest<{ agent: Agent }>(`${one(id)}/copy`, 'POST', {}).then((body) => body.agent),
  requestEditAccess: (id: string) =>
    companyRequest<{ status: string }>(`${one(id)}/edit-access`, 'POST', {}),
  decideEditAccess: (id: string, userId: string, grant: boolean) =>
    companyRequest<{ status: string }>(
      `${one(id)}/edit-access/${encodeURIComponent(userId)}`,
      'POST',
      { grant },
    ),
  removeEditor: (id: string, userId: string) =>
    companyRequest<{ removed: boolean }>(
      `${one(id)}/editors/${encodeURIComponent(userId)}`,
      'DELETE',
    ),
  updateSharing: (id: string, body: AgentSharing) =>
    companyRequest<{ agent: Agent | Pick<Agent, 'id' | 'scope' | 'enabled'> }>(
      `${one(id)}/sharing`,
      'PUT',
      body,
    ).then((result) => result.agent),
};

// The Agents page keeps its list in step with what the canvas beside it
// changes (switch on or off, rename, make personal, archive).
const AGENTS_CHANGED = 'poneva:agents-changed';
export function announceAgentsChanged() {
  window.dispatchEvent(new Event(AGENTS_CHANGED));
}
export function onAgentsChanged(listener: () => void) {
  window.addEventListener(AGENTS_CHANGED, listener);
  return () => window.removeEventListener(AGENTS_CHANGED, listener);
}

/** Whether a PATCH answer is the whole agent (you can still open it), not its list row. */
export const keepsAccess = (
  value: Agent | Pick<Agent, 'id' | 'scope' | 'enabled'>,
): value is Agent => 'access' in value;

// Server codes map to stable catalog keys; no server text is user-facing.
const AGENT_ERROR_KEYS: Record<string, keyof typeof workspaceKnowledgeRuntimeCopy.en> = {
  agent_unpublished: 'agentErrorAgent_unpublished',
  agent_revision_conflict: 'agentErrorAgent_revision_conflict',
  agent_run_queue_full: 'agentErrorAgent_run_queue_full',
  published_skill_required: 'agentErrorPublished_skill_required',
  invalid_schedule: 'agentErrorInvalid_schedule',
  schedule_too_frequent: 'agentErrorSchedule_too_frequent',
  agent_edit_access_exists: 'agentErrorAgent_edit_access_exists',
  agent_forbidden: 'agentErrorAgent_forbidden',
  agent_has_no_owner: 'agentErrorAgent_has_no_owner',
  agent_not_found: 'agentErrorAgent_not_found',
  agent_instructions_required: 'agentErrorAgent_instructions_required',
};
/**
 * Why a request failed, in words, when the answer says why: a known agent code,
 * or a refusal (403) or a change underneath (409). Otherwise null, and the caller
 * says what failed ("Couldn't run …") next to its Try again.
 */
export function knownAgentError(error: unknown, locale?: string): string | null {
  const { code, status } = (error ?? {}) as { code?: string; status?: number };
  const copy = namespaceCopyFor(workspaceKnowledgeRuntimeCopy, locale);
  if (code && AGENT_ERROR_KEYS[code]) return copy[AGENT_ERROR_KEYS[code]];
  return (status === 403 || status === 409) && error instanceof Error ? error.message : null;
}
export function agentErrorMessage(error: unknown, locale?: string) {
  return (
    knownAgentError(error, locale) ??
    (error instanceof Error
      ? error.message
      : namespaceCopyFor(workspaceKnowledgeRuntimeCopy, locale).agentErrorGeneric)
  );
}
